import './trivia-stage.css';
import { TypedStageEngagement } from '@/utils/engagementTypeUtils';
import {
  TriviaRoundStatus,
  TriviaSubmissionData,
  TriviaViewConfig,
  TriviaViewData,
} from '@/generated/graphql';
import { LetterTile } from '../LetterTile';
import { useTriviaFit } from '../useTriviaFit';
import { countdownRatio, useCountdown } from '../useCountdown';
import { choiceColor } from '../constants';
import { CheckIcon, LockIcon, PeopleIcon, TrophyIcon } from '../TriviaIcons';
import { cn } from '@/lib/utils';
import type { CSSProperties, ReactNode } from 'react';
import {
  triviaJoinBubbleText,
  triviaPlayersInLabel,
  triviaPlayersKickerLabel,
  triviaRevealResultLabel,
  triviaStageIsSkipped,
} from '../triviaChrome';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { stageToneFromColors } from '@/routes/stage/stageTone';

type Props = {
  engagement: TypedStageEngagement<
    TriviaViewConfig,
    TriviaViewData,
    TriviaSubmissionData
  >;
};

const RING_C = 2 * Math.PI * 52;

export function StageTriviaEngagement({ engagement }: Props) {
  const { state: themeState } = useScreenTheme();
  const tone = stageToneFromColors(
    themeState.savedTheme.foregroundColor,
    themeState.savedTheme.backgroundColor,
    themeState.savedTheme.stageBackgroundImage,
  );
  const view = engagement.viewData;
  const config = engagement.viewConfig;
  const status = view?.status ?? TriviaRoundStatus.Idle;
  const questions = config?.questions ?? [];
  const index = view?.questionIndex ?? 0;
  const question = questions[index];
  const { remainingMs } = useCountdown(view?.questionEndsAt);
  const skipped = triviaStageIsSkipped(view ?? undefined);
  const fitRef = useTriviaFit([
    question?.id,
    status,
    question?.text,
    question?.choices.length,
    skipped,
  ]);
  const n = question?.choices.length ?? 0;
  const tallies = view?.tallies ?? [];
  const tallyTotal = tallies.reduce((s, t) => s + t.count, 0);
  const tallyMap = new Map(tallies.map((t) => [t.choiceId, t.count]));
  const timerMs = question?.timerMs ?? config?.defaultTimerMs ?? 30_000;
  const ringRatio = countdownRatio(remainingMs, timerMs);
  const ringUrgent = ringRatio <= 1 / 3;
  const playerCount = view?.playerCount ?? 0;
  const answeredCount = view?.answeredCount ?? 0;

  if (status === TriviaRoundStatus.Idle) {
    const qCount = questions.length;
    const pillText = triviaJoinBubbleText(engagement.title);
    const headline =
      pillText === 'Trivia' && engagement.title
        ? engagement.title
        : 'Ready to play?';
    return (
      <div className="trivia-stage trivia-stage-idle" data-tone={tone}>
        <h1
          className={cn(
            'trivia-idle-title font-screen-heading',
            pillText === 'Trivia' && 'long',
          )}
          title={headline}
        >
          {headline}
        </h1>
        <p className="trivia-idle-sub font-screen-body">
          {qCount} questions · +1 point for every right answer
        </p>
        <ol className="trivia-steps">
          <li className="trivia-step">
            <b className="cA font-screen-heading">1</b>
            <span className="font-screen-body">
              Scan the code with your phone
            </span>
          </li>
          <li className="trivia-step">
            <b className="cB font-screen-heading">2</b>
            <span className="font-screen-body">
              Tap your answer before time runs out
            </span>
          </li>
          <li className="trivia-step">
            <b className="cC font-screen-heading">3</b>
            <span className="font-screen-body">
              Watch the big screen for the reveal
            </span>
          </li>
        </ol>
        <AudienceLine>{formatPlayersIn(playerCount)}</AudienceLine>
      </div>
    );
  }

  if (status === TriviaRoundStatus.RoundComplete) {
    const rows = view?.leaderboard ?? [];
    const coWinners = rows.filter((r) => r.rank === 1).length > 1;
    return (
      <div className="trivia-stage trivia-complete" data-tone={tone}>
        <div className="trivia-complete-head">
          <div className="trivia-confetti" aria-hidden>
            {CONFETTI.map((piece, i) => (
              <i key={i} style={piece} />
            ))}
          </div>
          <h1 className="font-screen-heading">
            {coWinners ? 'We have co-winners!' : 'Thanks for playing!'}
          </h1>
          <p className="trivia-kicker font-screen-body">
            {coWinners ? 'Tied for 1st' : 'Top scores'} · {questions.length}{' '}
            questions · {triviaPlayersKickerLabel(playerCount)}
          </p>
        </div>
        <div className="trivia-lb">
          {rows.map((row) => (
            <div
              key={row.userId}
              className={cn('trivia-lb-row', row.rank === 1 && 'gold')}
            >
              <span
                className={cn(
                  'rank font-screen-heading',
                  `r${Math.min(row.rank, 3)}`,
                )}
              >
                {row.rank}
              </span>
              <span className="nm font-screen-body">
                <span className="nm-text">{row.name}</span>
                {row.rank === 1 ? (
                  <TrophyIcon size={40} className="trophy" />
                ) : null}
                {row.rank === 1 && coWinners ? (
                  <span className="tag">CO-WINNER</span>
                ) : row.tied ? (
                  <span className="tag muted">TIE</span>
                ) : null}
              </span>
              <span className="sc font-screen-heading">
                {row.score}
                <small className="font-screen-body">
                  {' '}
                  / {questions.length}
                </small>
              </span>
            </div>
          ))}
          {(view?.leaderboardOverflow ?? 0) > 0 ? (
            <div className="trivia-lb-row overflow font-screen-body">
              <span className="nm">
                +{view?.leaderboardOverflow} more tied at 5th
              </span>
            </div>
          ) : null}
        </div>
        <p className="trivia-foot font-screen-body">
          Check your phone for your score and rank
        </p>
      </div>
    );
  }

  const questionLen = question?.text?.length ?? 0;

  return (
    <div className="trivia-stage" data-tone={tone}>
      <header className="trivia-qrow">
        <div className="trivia-chip font-screen-body">
          Question <strong className="font-screen-heading">{index + 1}</strong>{' '}
          of {questions.length}
        </div>
        <div className="trivia-sp" />
        {status === TriviaRoundStatus.QuestionOpen && view?.questionEndsAt ? (
          <div className={cn('trivia-ring', ringUrgent && 'urgent')}>
            <svg viewBox="0 0 120 120">
              <circle
                className="trivia-ring-track"
                cx="60"
                cy="60"
                r="52"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="60"
                cy="60"
                r="52"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={RING_C}
                strokeDashoffset={RING_C * (1 - ringRatio)}
              />
            </svg>
            <b className="font-screen-heading">
              {Math.ceil((remainingMs ?? 0) / 1000)}
            </b>
          </div>
        ) : null}
        {status === TriviaRoundStatus.QuestionClosed && skipped ? (
          <div className="trivia-chip skip font-screen-body">Skipped</div>
        ) : null}
        {status === TriviaRoundStatus.QuestionClosed && !skipped ? (
          <div className="trivia-chip lock font-screen-body">
            <LockIcon size={30} />
            Answers locked
          </div>
        ) : null}
        {status === TriviaRoundStatus.Revealed ? (
          <div className="trivia-chip ok font-screen-body">
            <CheckIcon size={30} /> The answer
          </div>
        ) : null}
      </header>
      <div className="trivia-fit" ref={fitRef} data-fitroot>
        <p
          className={cn(
            'trivia-q font-screen-body',
            questionLen > 90 && 'dense-q',
          )}
          data-base={questionLen > 90 ? 56 : 76}
        >
          {question?.text}
        </p>
        <div
          className={cn(
            'trivia-choices',
            `n${n}`,
            status === TriviaRoundStatus.QuestionClosed && 'closed',
          )}
        >
          {question?.choices.map((choice, i) => {
            const count = tallyMap.get(choice.id) ?? 0;
            const pct = tallyTotal ? Math.round((count / tallyTotal) * 100) : 0;
            const correct =
              !skipped &&
              status === TriviaRoundStatus.Revealed &&
              choice.id === view?.correctChoiceId;
            const dim =
              skipped ||
              (status === TriviaRoundStatus.Revealed &&
                choice.id !== view?.correctChoiceId);
            return (
              <div
                key={`${question?.id ?? 'q'}:${choice.id}:${i}`}
                className={cn(
                  'trivia-ch',
                  (status === TriviaRoundStatus.QuestionClosed || skipped) &&
                    'closed',
                  skipped && 'skipped',
                  correct && 'correct',
                  dim && 'dim',
                )}
              >
                {status === TriviaRoundStatus.Revealed && !skipped ? (
                  <span
                    className="fill"
                    style={{
                      width: `${pct}%`,
                      background: choiceColor(i),
                    }}
                  />
                ) : null}
                <span className="trivia-lt-wrap">
                  <LetterTile
                    index={i}
                    size={n > 4 ? 72 : 88}
                    className="lt font-screen-heading"
                  />
                  {correct ? (
                    <span className="ok">
                      <CheckIcon size={28} />
                    </span>
                  ) : null}
                </span>
                <span
                  className="tx font-screen-body"
                  data-base={n > 4 ? 38 : 52}
                >
                  {choice.text}
                </span>
                {status === TriviaRoundStatus.Revealed && !skipped ? (
                  <span className="pct">
                    <b className="font-screen-heading">{pct}%</b>
                    <span className="font-screen-body">
                      <PeopleIcon size={24} />
                      {count}
                    </span>
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      <div className="trivia-qfoot">
        {status === TriviaRoundStatus.QuestionOpen ? (
          <AudienceLine>{formatAnswersIn(answeredCount)}</AudienceLine>
        ) : null}
        {status === TriviaRoundStatus.QuestionClosed && !skipped ? (
          <>
            <p className="trivia-status font-screen-body">
              Time&apos;s up! Eyes on the screen…
            </p>
            <AudienceLine>{formatAnswersIn(answeredCount)}</AudienceLine>
          </>
        ) : null}
        {status === TriviaRoundStatus.QuestionClosed && skipped ? (
          <AudienceLine>{formatAnswersIn(answeredCount)}</AudienceLine>
        ) : null}
        {status === TriviaRoundStatus.Revealed && !skipped ? (
          <RevealResultLine
            correct={view?.correctCount ?? 0}
            answered={answeredCount}
          />
        ) : null}
        {status === TriviaRoundStatus.Revealed && skipped ? (
          <AudienceLine>{formatPlayersIn(playerCount)}</AudienceLine>
        ) : null}
      </div>
    </div>
  );
}

function RevealResultLine({
  correct,
  answered,
}: {
  correct: number;
  answered: number;
}) {
  const label = triviaRevealResultLabel(correct, answered);
  if (answered <= 0) {
    return <p className="trivia-status font-screen-body">{label}</p>;
  }
  const match = label.match(/^(\d+ of \d+)\s+(.*)$/);
  if (!match) {
    return <p className="trivia-status font-screen-body">{label}</p>;
  }
  return (
    <p className="trivia-status font-screen-body">
      <span className="ok-count">{match[1]}</span> {match[2]}
    </p>
  );
}

function AudienceLine({ children }: { children: ReactNode }) {
  return (
    <p className="trivia-audience font-screen-body">
      <PeopleIcon size={40} />
      <span>{children}</span>
    </p>
  );
}

function formatPlayersIn(count: number) {
  const label = triviaPlayersInLabel(count);
  if (count <= 0) return label;
  const suffix = count === 1 ? 'player in' : 'players in';
  return (
    <>
      <b>{count}</b> {suffix}
    </>
  );
}

function formatAnswersIn(count: number) {
  const suffix = count === 1 ? 'answer in' : 'answers in';
  return (
    <>
      <b>{count}</b> {suffix}
    </>
  );
}

const CONFETTI: Array<CSSProperties> = [
  // Beside headline — left cluster
  { left: '2%', top: '22%', background: '#FF00AA', transform: 'rotate(18deg)' },
  {
    left: '7%',
    top: '48%',
    background: '#00E5FF',
    transform: 'rotate(-28deg)',
  },
  {
    left: '0%',
    top: '68%',
    background: '#ADFF2F',
    transform: 'rotate(40deg)',
  },
  { left: '11%', top: '18%', background: '#fff', transform: 'rotate(8deg)' },
  {
    left: '13%',
    top: '62%',
    background: '#ADFF2F',
    transform: 'rotate(26deg)',
  },
  {
    left: '5%',
    top: '34%',
    background: '#00E5FF',
    transform: 'rotate(72deg)',
  },
  // Beside headline — right cluster
  {
    left: '87%',
    top: '20%',
    background: '#FF00AA',
    transform: 'rotate(-12deg)',
  },
  {
    left: '93%',
    top: '46%',
    background: '#00E5FF',
    transform: 'rotate(32deg)',
  },
  {
    left: '84%',
    top: '14%',
    background: '#fff',
    transform: 'rotate(-38deg)',
  },
  {
    left: '96%',
    top: '28%',
    background: '#ADFF2F',
    transform: 'rotate(55deg)',
  },
  {
    left: '90%',
    top: '66%',
    background: '#fff',
    transform: 'rotate(-65deg)',
  },
  {
    left: '82%',
    top: '52%',
    background: '#FF00AA',
    transform: 'rotate(-18deg)',
  },
];
